import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { STATUS_CONFIG, type Shipment } from '../../mocks/shipments';
import { fetchAllShipments } from '../../lib/shipmentsService';

const NYC_MAP = `https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d387140.4399660988!2d-74.0060!3d40.7128!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sus!4v1620000000000!5m2!1sen!2sus`;

export default function LiveMapPage() {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [selectedShipment, setSelectedShipment] = useState<Shipment | null>(null);
  const [mapMode, setMapMode] = useState<'fleet' | 'route'>('route');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAllShipments();
      setShipments(data);
      setSelectedShipment((prev) => prev ?? data[0] ?? null);
    } catch {
      setError('Unable to load shipments. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const shipmentMapUrl = selectedShipment
    ? `https://www.google.com/maps/embed?pb=!1m28!1m12!1m3!1d0!2d${(selectedShipment.origin.lng + selectedShipment.destination.lng) / 2}!3d${(selectedShipment.origin.lat + selectedShipment.destination.lat) / 2}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!4m13!3e0!4m5!1s0x0%3A0x0!2s${encodeURIComponent(selectedShipment.origin.city + ', ' + selectedShipment.origin.country)}!3m2!1d${selectedShipment.origin.lat}!2d${selectedShipment.origin.lng}!4m5!1s0x0%3A0x0!2s${encodeURIComponent(selectedShipment.destination.city + ', ' + selectedShipment.destination.country)}!3m2!1d${selectedShipment.destination.lat}!2d${selectedShipment.destination.lng}!5e0!3m2!1sen!2sus!4v1620000000000!5m2!1sen!2sus`
    : NYC_MAP;

  return (
    <div className="min-h-screen bg-[#1a1d29] font-sans">
      <Navbar />
      <main className="pt-16">
        {/* Header */}
        <div className="bg-[#1e2235] border-b border-white/5 px-6 py-6">
          <div className="max-w-full mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="font-heading text-2xl font-bold text-white">Live Road Map</h1>
              <p className="text-gray-400 text-sm mt-1">Real-time shipment location tracking on live road maps</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 bg-[#1a1d29] border border-white/10 rounded-lg px-1 py-1">
                {(['route', 'fleet'] as const).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setMapMode(mode)}
                    className={`px-4 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap capitalize ${
                      mapMode === mode ? 'bg-[#ff6b35] text-white' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    {mode === 'route' ? 'Route View' : 'Fleet View'}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1.5 px-3 py-2 bg-[#ff6b35]/10 border border-[#ff6b35]/20 rounded-lg">
                <span className="w-2 h-2 rounded-full bg-[#ff6b35] animate-pulse_soft" />
                <span className="text-[#ff6b35] text-xs font-medium">Live</span>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4">
            <div className="w-10 h-10 border-2 border-[#ff6b35] border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-400 text-sm">Loading live map...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4 text-center">
            <i className="ri-error-warning-line text-4xl text-red-400" />
            <p className="text-gray-300 text-sm">{error}</p>
            <button
              onClick={load}
              className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-[#ff6b35] text-white cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line mr-1.5" />Retry
            </button>
          </div>
        ) : shipments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 gap-3 text-center">
            <i className="ri-road-map-line text-5xl text-gray-600" />
            <p className="text-gray-400 text-sm">No active shipments to display.</p>
          </div>
        ) : (
          <div className="flex h-[calc(100vh-168px)] min-h-[600px]">
            {/* Sidebar */}
            <div className="w-80 shrink-0 bg-[#1e2235] border-r border-white/5 flex flex-col overflow-hidden">
              <div className="px-4 py-3 border-b border-white/5">
                <div className="flex items-center gap-2 bg-[#1a1d29] border border-white/10 rounded-lg px-3 py-2">
                  <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                    <i className="ri-search-line" />
                  </div>
                  <input type="text" placeholder="Search shipment..." className="bg-transparent text-white placeholder-gray-500 text-xs w-full outline-none" />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {shipments.map((s) => {
                  const cfg = STATUS_CONFIG[s.status];
                  const isSelected = selectedShipment?.id === s.id;
                  return (
                    <div
                      key={s.id}
                      onClick={() => setSelectedShipment(s)}
                      className={`p-3 rounded-xl border cursor-pointer transition-all ${
                        isSelected ? 'border-[#ff6b35]/40 bg-[#ff6b35]/5' : 'border-white/5 hover:border-white/15 bg-[#1a1d29]/40'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-mono text-xs font-semibold">{s.id}</span>
                        <span className={`text-xs ${cfg.color}`}>
                          <i className={`${cfg.icon} mr-1`} />
                          {cfg.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-gray-400 text-xs mb-2">
                        <span>{s.origin.city}</span>
                        <i className="ri-arrow-right-line text-gray-600" />
                        <span>{s.destination.city}</span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-1">
                        <div className="bg-[#ff6b35] h-1 rounded-full" style={{ width: `${s.status === 'delivered' ? 100 : s.status === 'out_for_delivery' ? 80 : s.status === 'in_transit' ? 55 : s.status === 'picked_up' ? 30 : 15}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Selected info */}
              {selectedShipment && (
                <div className="p-4 border-t border-white/5 bg-[#12141f]">
                  <p className="text-white text-sm font-semibold mb-1">{selectedShipment.id}</p>
                  <div className="flex items-center gap-2 text-xs text-gray-400 mb-2">
                    <i className="ri-user-line" />
                    <span>{selectedShipment.receiver.name}</span>
                  </div>
                  <button
                    onClick={() => navigate(`/track/${selectedShipment.id}`)}
                    className="w-full py-2 bg-[#ff6b35] hover:bg-[#ff8c5a] text-white text-xs font-semibold rounded-lg transition-all cursor-pointer"
                  >
                    View Full Details
                  </button>
                </div>
              )}
            </div>

            {/* Map area */}
            <div className="flex-1 relative">
              <iframe
                key={`${selectedShipment?.id ?? 'none'}-${mapMode}`}
                title="Live Road Map"
                src={mapMode === 'route' ? shipmentMapUrl : NYC_MAP}
                className="w-full h-full border-0"
                loading="lazy"
                allowFullScreen
              />

              {/* Floating overlay */}
              {selectedShipment && (
                <div className="absolute top-4 right-4 bg-[#1e2235]/90 backdrop-blur-xl border border-white/10 rounded-xl p-4 w-64">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white font-semibold text-sm">Active Route</span>
                    <span className="w-2 h-2 rounded-full bg-[#ff6b35] animate-pulse_soft" />
                  </div>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Tracking ID</span>
                      <span className="text-white font-mono">{selectedShipment.id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">From</span>
                      <span className="text-white">{selectedShipment.origin.city}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">To</span>
                      <span className="text-white">{selectedShipment.destination.city}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Status</span>
                      <span className={STATUS_CONFIG[selectedShipment.status].color}>
                        {STATUS_CONFIG[selectedShipment.status].label}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">ETA</span>
                      <span className="text-white">{selectedShipment.estimatedDelivery}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Bottom controls */}
              <div className="absolute bottom-4 right-4 flex flex-col gap-2">
                {[
                  { icon: 'ri-focus-3-line', label: 'Center' },
                  { icon: 'ri-add-line', label: 'Zoom In' },
                  { icon: 'ri-subtract-line', label: 'Zoom Out' },
                ].map((ctrl) => (
                  <button
                    key={ctrl.label}
                    title={ctrl.label}
                    className="w-10 h-10 flex items-center justify-center bg-[#1e2235]/90 backdrop-blur-sm border border-white/10 rounded-lg text-gray-300 hover:text-[#ff6b35] hover:border-[#ff6b35]/30 transition-all cursor-pointer"
                  >
                    <i className={`${ctrl.icon} text-lg`} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}